import { getRrp } from '@thg-commerce/enterprise-pricing/src'
import { ProductContentItem } from '@thg-commerce/enterprise-aurora-search/resolvers/SearchResults/types'

import { FulfilmentMethod, InStockLocation } from '../../generated/graphql'
import { getColourSwatches } from './colorSwatch'

export interface MarketedSpecialOffer {
  title?: {
    content: {
      content: string
    }[]
  } | null
  description?: {
    content: {
      content: string
    }[]
  } | null
  landingPageLink?: {
    text: string
    url: string
    openExternally?: boolean | null
    noFollow?: boolean | null
  } | null
  freeGiftProduct?: {
    sku: number
    title: string
    price?: {
      rrp: {
        displayValue: string
      }
    } | null
    images: {
      largeProduct?: string
    }[]
  } | null
}

interface ProductBlockResult {
  colourSwatches: ReturnType<typeof getColourSwatches>
  marketedSpecialOffer:
    | (MarketedSpecialOffer & {
        titleText: string
        descriptionText: string
      })
    | null
  inStock: boolean
  price: {
    price: {
      defaultPrice: string | null
      minPrice: string | null
      maxPrice: string | null
    }
    rrp: {
      defaultPrice: string | null
      minPrice: string | null
      maxPrice: string | null
    }
    __typename: string
  }
}

type ProductVariantLocal<ProductVariant> = ProductVariant & {
  choices: {
    key: string
    optionKey: string
  }[]
  inStock: boolean
  leadTime?: number
  weightGroups?: string[]
  isBookable?: boolean
  isCheckStock?: boolean
  isOrderInStore?: boolean
  inStockLocations?: string[] | null
  product?: {
    url?: string
  } | null
  price?: {
    price: {
      amount: string
      displayValue: string
    }
    rrp: {
      amount: string
      displayValue: string
    }
  } | null
  vipPrice?: {
    displayValue: string
    amount: string
  } | null
}

interface Product<VariantType, MarketedSpecialOfferType> {
  cheapestVariant?: ProductVariantLocal<VariantType> | null
  mostExpensiveVariant?: ProductVariantLocal<VariantType> | null
  marketedSpecialOffer?:
    | (MarketedSpecialOfferType & MarketedSpecialOffer)
    | null
  variants: ProductVariantLocal<VariantType>[]
  defaultVariant?:
    | (ProductVariantLocal<VariantType> & {
        eligibleForFulfilmentMethods?: string[] | null
        leadTime?: number
        weightGroups?: string[]
        isBookable?: boolean
        isCheckStock?: boolean
        isOrderInStore?: boolean
        inStockLocations?: InStockLocation[]
        marketedSpecialOffer?:
          | (MarketedSpecialOfferType & MarketedSpecialOffer)
          | null
      })
    | null
  options: {
    key: string
    choices: {
      colour?: string
      key: string
      title: string
    }[]
  }[]
  content: ProductContentItem
}

export const productBlock = <
  VariantType,
  MarketedSpecialOfferType,
  ProductType extends Product<VariantType, MarketedSpecialOfferType>
>(
  product: ProductType,
  defaultVariant: ProductVariantLocal<VariantType>,
): ProductType & ProductBlockResult => {
  const productMarketedSpecialOffer =
    product.marketedSpecialOffer || product.defaultVariant?.marketedSpecialOffer

  const transformedPrice = (variant: ProductVariantLocal<VariantType>) =>
    variant?.vipPrice
      ? {
          rrp: defaultVariant.price?.rrp,
          price: variant?.vipPrice,
          __typename: 'ProductPrice',
        }
      : variant?.price || null

  return {
    ...product,
    content: product.content,
    colourSwatches: getColourSwatches(product),
    price: {
      price: {
        defaultPrice: defaultVariant?.vipPrice?.displayValue
          ? defaultVariant.vipPrice.displayValue
          : defaultVariant?.price?.price?.displayValue || '0.00',
        minPrice: product.cheapestVariant?.vipPrice?.displayValue
          ? product.cheapestVariant.vipPrice.displayValue
          : product.cheapestVariant?.price?.price.displayValue || null,
        maxPrice: product.mostExpensiveVariant?.vipPrice?.displayValue
          ? product.mostExpensiveVariant.vipPrice.displayValue
          : product.mostExpensiveVariant?.price?.price.displayValue || null,
      },
      rrp: {
        defaultPrice:
          defaultVariant?.price?.price && defaultVariant?.price?.rrp
            ? getRrp(defaultVariant.price) || null
            : null,
        minPrice: product.cheapestVariant?.price?.rrp.displayValue || null,
        maxPrice: product.mostExpensiveVariant?.price?.rrp.displayValue || null,
      },
      __typename: 'ProductPrice',
    },
    cheapestVariant: product?.cheapestVariant
      ? {
          ...product.cheapestVariant,
          price: transformedPrice(product.cheapestVariant),
          vipPrice: product.cheapestVariant?.vipPrice
            ? product.cheapestVariant?.vipPrice
            : null,
        }
      : null,
    mostExpensiveVariant: product?.mostExpensiveVariant
      ? {
          ...product.mostExpensiveVariant,
          price: transformedPrice(product.mostExpensiveVariant),
          vipPrice: product.mostExpensiveVariant?.vipPrice
            ? product.mostExpensiveVariant?.vipPrice
            : null,
        }
      : null,
    defaultVariant: product?.defaultVariant
      ? {
          ...product.defaultVariant,
          price: transformedPrice(product.defaultVariant),
          vipPrice: product.defaultVariant?.vipPrice
            ? product.defaultVariant?.vipPrice
            : null,
        }
      : null,
    variants: product.variants.map((variant) => {
      return {
        ...variant,
        price: transformedPrice(variant),
        vipPrice: variant?.vipPrice ? variant?.vipPrice : null,
      }
    }),
    marketedSpecialOffer: !productMarketedSpecialOffer
      ? null
      : {
          ...productMarketedSpecialOffer,
          titleText:
            productMarketedSpecialOffer.title?.content[0].content || '',
          descriptionText:
            productMarketedSpecialOffer.description?.content[0].content || '',
          landingPageLink: productMarketedSpecialOffer.landingPageLink
            ? {
                text: productMarketedSpecialOffer.landingPageLink.text,
                url: productMarketedSpecialOffer.landingPageLink.url,
                openExternally:
                  productMarketedSpecialOffer.landingPageLink.openExternally ||
                  false,
                noFollow:
                  productMarketedSpecialOffer.landingPageLink.noFollow || true,
                __typename: 'Hyperlink',
              }
            : null,
          freeGiftProduct: productMarketedSpecialOffer.freeGiftProduct
            ? {
                sku: productMarketedSpecialOffer.freeGiftProduct.sku,
                title: productMarketedSpecialOffer.freeGiftProduct.title,
                price: {
                  rrp: {
                    displayValue:
                      productMarketedSpecialOffer.freeGiftProduct.price?.rrp
                        .displayValue || '',
                    __typename: 'FreeGiftProductDisplay',
                  },
                  __typename: 'FreeGiftProductPrice',
                },
                images:
                  productMarketedSpecialOffer.freeGiftProduct.images.map(
                    (image) => ({
                      largeProduct: image.largeProduct,
                      __typename: 'ProductImage',
                    }),
                  ) || [],
                __typename: 'FreeGiftProduct',
              }
            : null,
          __typename: 'ProductMarketedSpecialOffer',
        },
    inStockLocations: product.defaultVariant?.inStockLocations ?? null,
    leadTime: product.defaultVariant?.leadTime ?? null,
    weightGroups: product.defaultVariant?.weightGroups ?? null,
    isBookable: product.defaultVariant?.isBookable ?? null,
    inStock: product.variants.some((variant) => variant.inStock),
    isCheckStock:
      product.variants.some((variant) => variant.isCheckStock) ?? null,
    isOrderInStore:
      product.variants.some((variant) => variant.isOrderInStore) ?? null,
    icons: {
      homeDelivery: Boolean(
        product.defaultVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.HomeDelivery,
        ),
      ),
      storeAvailable: Boolean(
        product.defaultVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.CollectInStore,
        ),
      ),
      storeDelivery: Boolean(
        product.defaultVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.DeliverToStore,
        ),
      ),
      __typename: 'ProductDeliveryIcons',
    },
  }
}
